"use strict";
/**
 * THE CASH COUNTER'S CONCESSION — the rules that have to be the same in the
 * browser and on the server.
 *
 * Desk, 2026-10-12:
 *
 *   Staff          30%  employee picked from a list, or typed by employee code
 *   Staff dependent 30%  the staff member, then the relation (Spouse / Son /
 *                        Daughter); a son or daughter must not be above 21 years,
 *                        checked from the date of birth of the patient (OP number)
 *   Retired staff   30%  administrators only, the name is typed
 *   Camp            30%  the camp's name is typed
 *   Student         20%  the student's name is typed
 *
 * The PERCENTAGES are not here. They are `FinDiscountRule` rows an administrator
 * can change (Administration → Discount Rules), exactly as before. This file
 * holds only what is not a rate: who may use a category, which relations exist,
 * and the one age test. Pure by construction — no Prisma, no clock of its own —
 * so the screen that greys a choice out and the server that refuses it cannot
 * disagree.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CHILD_DEPENDENT_MAX_AGE_YEARS = exports.DEPENDENT_RELATION_LABEL = exports.DEPENDENT_RELATIONS = exports.CONCESSION_ADMIN_ROLES = void 0;
exports.isConcessionAdmin = isConcessionAdmin;
exports.ageInCompletedYears = ageInCompletedYears;
exports.dependentConcessionVerdict = dependentConcessionVerdict;
/** Logins that see the Retired-staff category and the bill-level Discount box. */
exports.CONCESSION_ADMIN_ROLES = ['SuperAdmin', 'System Administrator'];
function isConcessionAdmin(roles) {
    return (roles ?? []).some((role) => exports.CONCESSION_ADMIN_ROLES.includes(role));
}
/** The relations a staff dependent can stand in. Spouse has no age test. */
exports.DEPENDENT_RELATIONS = ['SPOUSE', 'SON', 'DAUGHTER'];
exports.DEPENDENT_RELATION_LABEL = {
    SPOUSE: 'Spouse',
    SON: 'Son',
    DAUGHTER: 'Daughter',
};
/** A son or daughter is eligible up to and including this age in completed years. */
exports.CHILD_DEPENDENT_MAX_AGE_YEARS = 21;
/** Hospital clock: Asia/Kolkata is UTC+05:30 with no daylight saving. */
const IST_OFFSET_MS = 330 * 60 * 1000;
/**
 * Age in COMPLETED years on `asOf`, by calendar date in India.
 *
 * `dateOfBirth` is a DATE column (UTC midnight of the stored day), so its UTC
 * parts are the day that was entered. Returns null for a date in the future.
 */
function ageInCompletedYears(dateOfBirth, asOf) {
    const dob = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;
    if (Number.isNaN(dob.getTime()))
        return null;
    const today = new Date(asOf.getTime() + IST_OFFSET_MS);
    let years = today.getUTCFullYear() - dob.getUTCFullYear();
    const beforeBirthday = today.getUTCMonth() < dob.getUTCMonth() ||
        (today.getUTCMonth() === dob.getUTCMonth() && today.getUTCDate() < dob.getUTCDate());
    if (beforeBirthday)
        years -= 1;
    return years < 0 ? null : years;
}
/**
 * May this patient be given the staff-dependent concession in this relation?
 *
 * SPOUSE: yes, no age test. SON / DAUGHTER: needs a date of birth on the
 * patient's record and must not be above 21 years. A missing date of birth is a
 * refusal, not a pass — an unverifiable child is the exact case the age test
 * exists to catch.
 */
function dependentConcessionVerdict(input) {
    if (input.relation === 'SPOUSE')
        return { ok: true };
    if (!input.dateOfBirth) {
        return {
            ok: false,
            reason: 'This patient has no date of birth on file, so a son or daughter’s age cannot be checked. ' +
                'Correct the date of birth first.',
        };
    }
    const age = ageInCompletedYears(input.dateOfBirth, input.asOf);
    if (age == null) {
        return { ok: false, reason: 'This patient’s date of birth is not a valid past date.' };
    }
    if (age > exports.CHILD_DEPENDENT_MAX_AGE_YEARS) {
        return {
            ok: false,
            reason: `A son or daughter is eligible only up to ${exports.CHILD_DEPENDENT_MAX_AGE_YEARS} years; ` +
                `this patient is ${age} years.`,
        };
    }
    return { ok: true };
}
//# sourceMappingURL=staff-concession.js.map